/**
 * /admin/clip-assignments/insights — how the clipping work is going.
 *
 *   GET /api/clipping/review/insights/          every assignment + daily stamps
 *   GET /api/clipping/review/insights/aws/      is the video / are the clips in S3
 *   GET /api/clipping/review/insights/export/   the same numbers as .xlsx
 *
 * One fetch; every filter, total and chart below is computed here from
 * the assignment rows, so changing a filter never waits on the server.
 * The Excel download is built fresh by the backend from the same
 * builder, so it is always up to date and always matches the page.
 *
 * The two AWS columns come from their own request: they ask S3 itself,
 * which takes several seconds uncached, so the charts don't wait on them.
 * The backend caches that answer for five minutes; Refresh skips it.
 *
 * "Active hours" counts only gaps of 15 minutes or less between stamps,
 * so breaks and overnight pauses don't make a clipper look slow.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, BarChart3, Download, RefreshCw, Search, ArrowUpDown,
} from "lucide-react";
import api from "../../api/axios";
import { Card } from "./_shared/Card";

const BASE = "/api/clipping/review/insights/";

// Status → colour. Fixed per status (never by rank), and ordered so the
// stacked bars put validated-adjacent hues side by side. Every use also
// carries a legend and a count, so colour is never the only cue.
const STATUS_STYLE = {
  APPROVED:    { color: "var(--series-3)" },
  SUBMITTED:   { color: "var(--series-2)" },
  IN_PROGRESS: { color: "var(--series-1)" },
  REJECTED:    { color: "var(--series-5)" },
  ASSIGNED:    { color: "var(--series-other)" },
};
const STACK_ORDER = ["APPROVED", "SUBMITTED", "IN_PROGRESS", "REJECTED", "ASSIGNED"];

const RANGES = [
  { key: "all", label: "All time", days: null },
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
];

const n = (v) => (v == null ? "—" : Number(v).toLocaleString("en-IN"));
const hrs = (v) => (v == null ? "—" : v < 48 ? `${v} h` : `${(v / 24).toFixed(1)} d`);
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—");
const when = (iso) => (iso
  ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
  : "—");

function rollup(rows, key) {
  const m = new Map();
  rows.forEach((r) => {
    const k = r[key];
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(r);
  });
  return [...m.entries()].map(([k, rs]) => {
    const stamped = rs.reduce((s, r) => s + r.balls_stamped, 0);
    const total = rs.reduce((s, r) => s + r.balls_total, 0);
    const active = rs.reduce((s, r) => s + r.active_hours, 0);
    const avg = (f) => {
      const v = rs.map((r) => r[f]).filter((x) => x != null);
      return v.length ? Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10 : null;
    };
    const byStatus = {};
    rs.forEach((r) => { byStatus[r.status] = (byStatus[r.status] || 0) + 1; });
    return {
      key: k,
      name: key === "clipper_email" ? rs[0].clipper : k,
      kind: rs[0].clipper_kind,
      assignments: rs.length,
      byStatus,
      stamped, total,
      pct: total ? Math.round((100 * stamped) / total) : 0,
      recuts: rs.reduce((s, r) => s + r.recuts, 0),
      active: Math.round(active * 10) / 10,
      bph: active >= 0.2 ? Math.round(stamped / active) : null,
      submit: avg("hours_to_submit"),
      review: avg("hours_in_review"),
      ready: rs.reduce((s, r) => s + r.clips_ready, 0),
      failed: rs.reduce((s, r) => s + r.clips_failed, 0),
    };
  }).sort((a, b) => b.stamped - a.stamped);
}

export default function ClipInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [comp, setComp] = useState("");
  const [clipper, setClipper] = useState("");
  const [status, setStatus] = useState("");
  const [range, setRange] = useState("all");
  const [tip, setTip] = useState(null);
  const [aws, setAws] = useState(null);
  const [awsError, setAwsError] = useState(null);

  async function loadAws(fresh) {
    setAws(null);
    setAwsError(null);
    try {
      const { data } = await api.get(`${BASE}aws/`, { params: fresh ? { fresh: 1 } : {} });
      setAws(data.assignments || {});
    } catch (e) {
      setAwsError(e?.response?.status || e.message);
    }
  }

  async function load(fresh = false) {
    setLoading(true);
    setError(null);
    loadAws(fresh);
    try {
      const { data } = await api.get(BASE);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function download() {
    setDownloading(true);
    setError(null);
    try {
      const resp = await api.get(`${BASE}export/`, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(resp.headers?.["content-disposition"] || "")?.[1]
        || "clip-assignments-insights.xlsx";
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Download failed: ${e?.response?.status || e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  const all = useMemo(() => data?.assignments || [], [data]);
  const statuses = useMemo(() => data?.statuses || [], [data]);
  const statusLabel = useMemo(
    () => Object.fromEntries(statuses.map((s) => [s.key, s.label])), [statuses],
  );
  const competitions = useMemo(() => [...new Set(all.map((r) => r.competition))].sort(), [all]);
  const clippers = useMemo(() => rollup(all, "clipper_email").map((c) => ({ key: c.key, name: c.name }))
    .sort((a, b) => a.name.localeCompare(b.name)), [all]);

  const rows = useMemo(() => {
    const days = RANGES.find((r) => r.key === range)?.days;
    const since = days ? Date.now() - days * 864e5 : null;
    return all.filter((r) => (!comp || r.competition === comp)
      && (!clipper || r.clipper_email === clipper)
      && (!status || r.status === status)
      && (!since || new Date(r.created_at).getTime() >= since));
  }, [all, comp, clipper, status, range]);

  const totals = useMemo(() => {
    const s = (f) => rows.reduce((a, r) => a + (r[f] || 0), 0);
    const by = {};
    rows.forEach((r) => { by[r.status] = (by[r.status] || 0) + 1; });
    return {
      assignments: rows.length,
      matches: new Set(rows.map((r) => `${r.competition}|${r.match}`)).size,
      clippers: new Set(rows.map((r) => r.clipper_email)).size,
      stamped: s("balls_stamped"), total: s("balls_total"),
      ready: s("clips_ready"), failed: s("clips_failed"), processing: s("clips_processing"),
      by,
    };
  }, [rows]);

  const byClipper = useMemo(() => rollup(rows, "clipper_email"), [rows]);
  const byComp = useMemo(() => rollup(rows, "competition"), [rows]);

  const daily = useMemo(() => {
    const ids = new Set(rows.map((r) => r.id));
    const windowDays = RANGES.find((r) => r.key === range)?.days || 30;
    const out = [];
    const sums = {};
    (data?.daily || []).forEach((d) => {
      if (ids.has(d.assignment_id)) sums[d.date] = (sums[d.date] || 0) + d.balls;
    });
    const today = new Date();
    for (let i = windowDays - 1; i >= 0; i -= 1) {
      const dt = new Date(today.getTime() - i * 864e5);
      const key = dt.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
      out.push({ date: key, balls: sums[key] || 0 });
    }
    return out;
  }, [data, rows, range]);

  const showTip = (e, content) => setTip({ x: e.clientX, y: e.clientY, content });
  const hideTip = () => setTip(null);

  return (
    <div className="p-6 space-y-5" onMouseLeave={hideTip}>
      <header className="flex items-center gap-3 flex-wrap">
        <Link
          to="/admin/clip-assignments"
          className="text-text-secondary hover:text-text-primary"
          aria-label="Back to clip assignments"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <BarChart3 className="w-6 h-6 text-brand" />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold">Clip insights</h1>
          <p className="text-sm text-text-secondary">
            What has been assigned, stamped, reviewed and cut — by clipper,
            by competition and by day.
            {data?.generated_at && <> Updated {when(data.generated_at)} IST.</>}
          </p>
        </div>
        <button className="btn-secondary inline-flex items-center gap-2" onClick={() => load(true)} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button
          className="btn-primary inline-flex items-center gap-2"
          onClick={download}
          disabled={downloading}
          title="Every assignment, clipper and competition, built fresh from live data"
        >
          <Download className="w-4 h-4" /> {downloading ? "Preparing…" : "Download Excel"}
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <Select value={comp} onChange={setComp} all="All competitions"
          options={competitions.map((c) => ({ value: c, label: c }))} />
        <Select value={clipper} onChange={setClipper} all="All clippers"
          options={clippers.map((c) => ({ value: c.key, label: c.name }))} />
        <Select value={status} onChange={setStatus} all="All statuses"
          options={statuses.map((s) => ({ value: s.key, label: s.label }))} />
        <Select value={range} onChange={setRange}
          options={RANGES.map((r) => ({ value: r.key, label: r.key === "all" ? "Assigned: all time" : `Assigned: ${r.label.toLowerCase()}` }))} />
        {(comp || clipper || status || range !== "all") && (
          <button
            className="text-sm text-text-secondary hover:text-text-primary underline"
            onClick={() => { setComp(""); setClipper(""); setStatus(""); setRange("all"); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && !data ? (
        <div className="text-sm text-text-secondary">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Card label="Matches assigned" value={n(totals.matches)}
              hint={`${n(totals.assignments)} assignments · ${n(totals.clippers)} clippers`} />
            <Card label="Balls stamped" value={n(totals.stamped)}
              hint={`of ${n(totals.total)} · ${totals.total ? Math.round((100 * totals.stamped) / totals.total) : 0}%`} />
            <Card label="Approved" value={n(totals.by.APPROVED || 0)} hint="clips cut" />
            <Card label="Awaiting review" value={n(totals.by.SUBMITTED || 0)}
              hint={totals.by.REJECTED ? `${totals.by.REJECTED} sent back` : "submitted by clippers"} />
            <Card label="In progress" value={n((totals.by.IN_PROGRESS || 0))}
              hint={`${n(totals.by.ASSIGNED || 0)} not started`} />
            <Card label="Clips cut" value={n(totals.ready)}
              hint={totals.failed ? `${n(totals.failed)} failed` : totals.processing ? `${n(totals.processing)} processing` : "none failed"} />
          </div>

          <Panel title="Pipeline" subtitle="Assignments by status">
            <StackBar counts={totals.by} total={totals.assignments} statusLabel={statusLabel}
              onTip={showTip} offTip={hideTip} height={28} />
            <Legend statusLabel={statusLabel} counts={totals.by} />
          </Panel>

          <Panel
            title="Balls stamped per day"
            subtitle={`IST, last ${daily.length} days${range === "all" ? "" : " (follows the date filter)"}`}
          >
            <DailyChart days={daily} onTip={showTip} offTip={hideTip} />
          </Panel>

          <div className="grid xl:grid-cols-2 gap-5">
            <Panel title="Clippers" subtitle="Balls stamped, and speed while actively clipping">
              <ClipperBars rows={byClipper} onTip={showTip} offTip={hideTip} />
            </Panel>
            <Panel title="Competitions" subtitle="Assignments by status in each competition">
              <div className="space-y-3">
                {byComp.map((c) => (
                  <div key={c.key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-text-primary truncate">{c.name}</span>
                      <span className="text-text-secondary font-mono shrink-0 ml-2">
                        {c.byStatus.APPROVED || 0}/{c.assignments} approved · {c.pct}% stamped
                      </span>
                    </div>
                    <StackBar counts={c.byStatus} total={c.assignments} statusLabel={statusLabel}
                      onTip={showTip} offTip={hideTip} height={14} context={c.name} />
                  </div>
                ))}
                {!byComp.length && <Empty />}
              </div>
              <Legend statusLabel={statusLabel} />
            </Panel>
          </div>

          <Panel title="Clipper performance" subtitle="Active hours count only gaps of 15 minutes or less between stamps">
            <ClipperTable rows={byClipper} />
          </Panel>

          <Panel
            title="Every assignment"
            subtitle={`${rows.length} shown · the AWS columns are checked against S3 itself`}
          >
            <AssignmentTable rows={rows} aws={aws} awsError={awsError} />
          </Panel>
        </>
      )}

      {tip && (
        <div
          className="fixed z-50 pointer-events-none rounded-md border border-border-default bg-bg-primary px-3 py-2 text-xs shadow-lg"
          style={{ left: tip.x + 14, top: tip.y + 14 }}
        >
          {tip.content}
        </div>
      )}
    </div>
  );
}

function Select({ value, onChange, options, all }) {
  return (
    <select
      className="rounded-md border border-border-default bg-bg-primary px-3 py-1.5 text-sm"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {all && <option value="">{all}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Panel({ title, subtitle, children }) {
  return (
    <section className="rounded-lg border border-border-default bg-bg-primary p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {subtitle && <p className="text-xs text-text-secondary">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function Empty() {
  return <div className="text-sm text-text-secondary">Nothing matches these filters.</div>;
}

function Swatch({ color }) {
  return <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />;
}

function Legend({ statusLabel, counts }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-secondary">
      {STACK_ORDER.map((s) => (
        <span key={s} className="inline-flex items-center gap-1.5">
          <Swatch color={STATUS_STYLE[s].color} />
          {statusLabel[s] || s}
          {counts && <span className="text-text-primary font-mono">{counts[s] || 0}</span>}
        </span>
      ))}
    </div>
  );
}

// One horizontal bar split by status. 2px gaps between segments.
function StackBar({ counts, total, statusLabel, onTip, offTip, height, context }) {
  if (!total) return <div className="rounded bg-bg-secondary" style={{ height }} />;
  return (
    <div className="flex gap-[2px] w-full" style={{ height }}>
      {STACK_ORDER.filter((s) => counts[s]).map((s, i, arr) => (
        <div
          key={s}
          className={`${i === 0 ? "rounded-l" : ""} ${i === arr.length - 1 ? "rounded-r" : ""} cursor-default hover:opacity-80`}
          style={{ width: `${(100 * counts[s]) / total}%`, background: STATUS_STYLE[s].color, minWidth: 3 }}
          onMouseMove={(e) => onTip(e, (
            <>
              {context && <div className="text-text-secondary">{context}</div>}
              <div><span className="font-medium">{statusLabel[s] || s}</span>: {counts[s]} of {total}
                {" "}({Math.round((100 * counts[s]) / total)}%)</div>
            </>
          ))}
          onMouseLeave={offTip}
        />
      ))}
    </div>
  );
}

function DailyChart({ days, onTip, offTip }) {
  const max = Math.max(1, ...days.map((d) => d.balls));
  const total = days.reduce((s, d) => s + d.balls, 0);
  const active = days.filter((d) => d.balls).length;
  const peak = days.reduce((a, d) => (d.balls > a.balls ? d : a), days[0] || { balls: 0 });
  // A rounded "nice" top for the axis.
  const step = 10 ** Math.floor(Math.log10(max));
  const top = Math.ceil(max / step) * step;
  const H = 160;
  return (
    <div className="space-y-2">
      <div className="text-xs text-text-secondary">
        <span className="text-text-primary font-mono">{n(total)}</span> balls ·{" "}
        {active} active days{peak?.balls ? <> · peak {n(peak.balls)} on {day(peak.date)}</> : null}
      </div>
      <div className="flex">
        <div className="flex flex-col justify-between text-[10px] text-text-secondary font-mono pr-2 text-right" style={{ height: H }}>
          <span>{n(top)}</span><span>{n(top / 2)}</span><span>0</span>
        </div>
        <div className="relative flex-1" style={{ height: H }}>
          {[0, 0.5, 1].map((f) => (
            <div key={f} className="absolute left-0 right-0 border-t border-border-default/60" style={{ top: `${f * 100}%` }} />
          ))}
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {days.map((d) => (
              <div
                key={d.date}
                className="flex-1 h-full flex items-end group cursor-default"
                onMouseMove={(e) => onTip(e, (
                  <><div className="text-text-secondary">{day(d.date)}</div>
                    <div><span className="font-mono">{n(d.balls)}</span> balls stamped</div></>
                ))}
                onMouseLeave={offTip}
              >
                <div
                  className="w-full rounded-t group-hover:opacity-80"
                  style={{ height: `${(100 * d.balls) / top}%`, background: "var(--series-1)", minHeight: d.balls ? 2 : 0 }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-text-secondary font-mono pl-8">
        <span>{day(days[0]?.date)}</span>
        <span>{day(days[Math.floor(days.length / 2)]?.date)}</span>
        <span>{day(days[days.length - 1]?.date)}</span>
      </div>
    </div>
  );
}

function ClipperBars({ rows, onTip, offTip }) {
  if (!rows.length) return <Empty />;
  const maxBalls = Math.max(1, ...rows.map((r) => r.stamped));
  const maxBph = Math.max(1, ...rows.map((r) => r.bph || 0));
  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-[minmax(0,9rem)_1fr_1fr] gap-3 text-[10px] uppercase tracking-widest text-text-secondary">
        <span />
        <span>Balls stamped</span>
        <span>Balls / active hour</span>
      </div>
      {rows.map((r) => (
        <div key={r.key} className="grid grid-cols-[minmax(0,9rem)_1fr_1fr] gap-3 items-center text-sm">
          <span className="truncate" title={r.key}>{r.name}</span>
          <Bar value={r.stamped} max={maxBalls} label={n(r.stamped)} color="var(--series-1)"
            onTip={(e) => onTip(e, (<><div className="font-medium">{r.name}</div>
              <div>{n(r.stamped)} of {n(r.total)} balls stamped ({r.pct}%)</div>
              <div>{r.byStatus.APPROVED || 0} of {r.assignments} assignments approved</div></>))}
            offTip={offTip} />
          <Bar value={r.bph || 0} max={maxBph} label={r.bph == null ? "—" : n(r.bph)} color="var(--series-1)"
            onTip={(e) => onTip(e, (<><div className="font-medium">{r.name}</div>
              <div>{r.bph == null ? "Not enough stamps to time" : `${n(r.bph)} balls per active hour`}</div>
              <div>{r.active} active hours</div></>))}
            offTip={offTip} />
        </div>
      ))}
    </div>
  );
}

function Bar({ value, max, label, color, onTip, offTip }) {
  return (
    <div className="flex items-center gap-2 cursor-default" onMouseMove={onTip} onMouseLeave={offTip}>
      <div className="flex-1 h-3">
        <div className="h-full rounded-r" style={{ width: `${(100 * value) / max}%`, background: color, minWidth: value ? 2 : 0 }} />
      </div>
      <span className="font-mono text-xs text-text-secondary w-12 text-right shrink-0">{label}</span>
    </div>
  );
}

function useSort(rows, initial) {
  const [sort, setSort] = useState(initial);
  const sorted = useMemo(() => {
    const [k, dir] = sort;
    return [...rows].sort((a, b) => {
      const x = a[k]; const y = b[k];
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const c = typeof x === "number" ? x - y : String(x).localeCompare(String(y));
      return dir === "asc" ? c : -c;
    });
  }, [rows, sort]);
  const th = (k, label, right) => (
    <th
      className={`px-2 py-2 font-medium whitespace-nowrap cursor-pointer select-none hover:text-text-primary ${right ? "text-right" : "text-left"}`}
      onClick={() => setSort(([pk, pd]) => [k, pk === k && pd === "desc" ? "asc" : "desc"])}
    >
      <span className="inline-flex items-center gap-1">
        {label}<ArrowUpDown className={`w-3 h-3 ${sort[0] === k ? "opacity-100" : "opacity-30"}`} />
      </span>
    </th>
  );
  return [sorted, th];
}

function ClipperTable({ rows }) {
  const flat = useMemo(() => rows.map((r) => ({
    ...r,
    approved: r.byStatus.APPROVED || 0,
    waiting: r.byStatus.SUBMITTED || 0,
    open: (r.byStatus.IN_PROGRESS || 0) + (r.byStatus.ASSIGNED || 0) + (r.byStatus.REJECTED || 0),
  })), [rows]);
  const [sorted, th] = useSort(flat, ["stamped", "desc"]);
  if (!rows.length) return <Empty />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-text-secondary border-b border-border-default">
          <tr>
            {th("name", "Clipper")}{th("kind", "Type")}{th("assignments", "Assigned", true)}
            {th("approved", "Approved", true)}{th("waiting", "Awaiting review", true)}{th("open", "Open", true)}
            {th("stamped", "Balls stamped", true)}{th("pct", "% done", true)}{th("recuts", "Re-cuts", true)}
            {th("active", "Active h", true)}{th("bph", "Balls/h", true)}
            {th("submit", "Assign→submit", true)}{th("review", "Submit→review", true)}
            {th("ready", "Clips cut", true)}{th("failed", "Failed", true)}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.key} className="border-b border-border-default/50 hover:bg-bg-secondary/40">
              <td className="px-2 py-1.5"><div>{r.name}</div><div className="text-xs text-text-secondary">{r.key}</div></td>
              <td className="px-2 py-1.5 text-text-secondary">{r.kind}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.assignments)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.approved)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.waiting)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.open)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.stamped)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{r.pct}%</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.recuts)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{r.active}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.bph)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{hrs(r.submit)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{hrs(r.review)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{n(r.ready)}</td>
              <td className={`px-2 py-1.5 text-right font-mono ${r.failed ? "text-rose-400" : ""}`}>{n(r.failed)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Good / partial / bad for the two AWS columns. Text always carries the
// meaning; the dot only helps scanning.
const AWS_TONE = {
  yes: "bg-emerald-400", partly: "bg-amber-400", no: "bg-rose-400", none: "bg-text-secondary/40",
};
function awsTone(label) {
  if (!label || label === "Unknown") return null;
  if (label.startsWith("Yes")) return "yes";
  if (label.startsWith("Partly")) return "partly";
  if (label.startsWith("No ·") || label === "No") return "no";
  return "none";
}

function AwsCell({ label, title, pending, failed }) {
  if (failed) return <span className="text-text-secondary">Check failed</span>;
  if (pending) return <span className="text-text-secondary animate-pulse">Checking…</span>;
  const tone = awsTone(label);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap" title={title}>
      {tone && <span className={`inline-block w-2 h-2 rounded-full ${AWS_TONE[tone]}`} />}
      {label || "—"}
    </span>
  );
}

function AssignmentTable({ rows: base, aws, awsError }) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => base.map((r) => ({ ...r, ...(aws?.[r.id] || {}) })), [base, aws]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => `${r.competition} ${r.match} ${r.clipper} ${r.clipper_email} ${r.status_label}`
      .toLowerCase().includes(t));
  }, [rows, q]);
  const [sorted, th] = useSort(filtered, ["created_at", "desc"]);
  return (
    <div className="space-y-2">
      <label className="relative block max-w-sm">
        <Search className="w-4 h-4 absolute left-2.5 top-2 text-text-secondary" />
        <input
          className="w-full rounded-md border border-border-default bg-bg-primary pl-8 pr-3 py-1.5 text-sm"
          placeholder="Search match, team, clipper…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      {!sorted.length ? <Empty /> : (
        <div className="overflow-x-auto max-h-[36rem] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-text-secondary border-b border-border-default sticky top-0 bg-bg-primary">
              <tr>
                {th("competition", "Competition")}{th("match_number", "#", true)}{th("match", "Match")}
                {th("clipper", "Clipper")}{th("status_label", "Status")}{th("scope", "Scope")}
                {th("balls_stamped", "Stamped", true)}{th("pct_complete", "%", true)}{th("recuts", "Re-cuts", true)}
                {th("avg_clip_s", "Avg clip", true)}{th("balls_per_hour", "Balls/h", true)}
                {th("created_at", "Assigned")}{th("submitted_at", "Submitted")}{th("reviewed_at", "Reviewed")}
                {th("clips_ready", "Cut", true)}{th("clips_failed", "Failed", true)}
                {th("video_in_aws", "Full video in AWS")}{th("clips_in_aws", "BBB clips in AWS")}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} className="border-b border-border-default/50 hover:bg-bg-secondary/40">
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.competition}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.match_number ?? "—"}</td>
                  <td className="px-2 py-1.5 min-w-[16rem]">{r.match.replace(/^Match \d+ · /, "")}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{r.clipper}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <Swatch color={STATUS_STYLE[r.status]?.color} />{r.status_label}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-text-secondary">{r.scope}</td>
                  <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{n(r.balls_stamped)}/{n(r.balls_total)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.pct_complete}%</td>
                  <td className="px-2 py-1.5 text-right font-mono">{n(r.recuts)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.avg_clip_s == null ? "—" : `${r.avg_clip_s}s`}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{n(r.balls_per_hour)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-text-secondary">{day(r.created_at)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-text-secondary">{day(r.submitted_at)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-text-secondary">{day(r.reviewed_at)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{n(r.clips_ready)}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${r.clips_failed ? "text-rose-400" : ""}`}>{n(r.clips_failed)}</td>
                  <td className="px-2 py-1.5">
                    <AwsCell
                      label={r.video_in_aws}
                      pending={!aws}
                      failed={awsError}
                      title={r.video_storage_class ? `S3 storage class: ${r.video_storage_class}` : undefined}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <AwsCell
                      label={r.clips_in_aws}
                      pending={!aws}
                      failed={awsError}
                      title={r.clips_expected ? `${n(r.clips_in_s3)} of ${n(r.clips_expected)} clip files found in S3` : undefined}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
